import React, { Component } from 'react';
import { push } from '../../../../navigation';
import { bindActionCreators } from 'redux';
import { connect } from 'react-redux';
import { Media } from 'src/utils/Media';

import { withRouter } from 'react-router-dom';
import ReactGA from 'react-ga';
import { withTranslation } from 'react-i18next';
import { Button } from '../../../../components/designSystem';
import AppIcon from '../../../../components/AppIcon';

import { updateSpineIndex } from '../../../../modules/readerApp';
import { submitSurveyAnswers } from '../../../../modules/survey';

import language from '../../../../config/language';

// tracking
import { COMPLETED_BOOK, track } from '../../../../utils/tracker';
import { getFreshIdToken } from '../../../../utils/authUtil';

export class ChapterMenu extends Component {
  constructor(props) {
    super(props);
    this.state = {
      currentItemPercentagePassed: undefined,
    };
  }

  canMoveForward = () => {
    const { currentBook, currentSpineIndex, answers = {} } = this.props;
    const currentItem = currentBook?.parts?.[currentSpineIndex];
    if (!currentItem) return false;
    if (currentItem.kind === 'Chapter') return true;
    if (currentItem.kind !== 'Survey') return false;

    // The reader survey reducer stores the current survey's answers as a flat
    // array. Keep accepting the older keyed shape as well because a few legacy
    // callers still construct ChapterMenu directly.
    const surveyAnswers = Array.isArray(answers)
      ? answers
      : answers[currentItem._id] || [];
    return (currentItem.questions || []).every(question => {
      if (!question.required) return true;
      const answer = surveyAnswers.find(
        candidate => candidate.surveyQuestion === question._id
      );
      return (
        answer?.answer !== undefined &&
        answer?.answer !== null &&
        String(answer.answer).trim() !== ''
      );
    });
  };

  moveBack = event => {
    event.preventDefault();
    const { currentSpineIndex, updateSpineIndex } = this.props;
    if (currentSpineIndex <= 0) {
      // at beginning of book, do nothing
      return;
    }
    updateSpineIndex(currentSpineIndex - 1);
    const main = document.getElementById('main');
    ReactGA.event({
      category: 'Reading',
      action: 'Moved back',
      value: currentSpineIndex - 1,
    });
    document.getElementById('main').scrollTop = 0;
  };

  moveForward = event => {
    event.preventDefault();
    event.stopPropagation();
    if (!this.canMoveForward()) return;

    const {
      currentBook,
      currentSpineIndex,
      onSurveyComplete,
      updateSpineIndex,
    } = this.props;
    if (currentSpineIndex >= currentBook.parts.length - 1) {
      // at end of book, do nothing
      return;
    }
    if (
      currentBook.parts[currentSpineIndex].kind === 'Survey' &&
      onSurveyComplete
    ) {
      onSurveyComplete();
    }
    updateSpineIndex(currentSpineIndex + 1);
    ReactGA.event({
      category: 'Reading',
      action: 'Moved forward',
      value: currentSpineIndex + 1,
    });
    document.getElementById('main').scrollTop = 0;
  };

  handleForward = event => {
    event.preventDefault();
    event.stopPropagation();
    if (!this.canMoveForward()) return;

    const { currentBook, currentSpineIndex } = this.props;
    if (currentSpineIndex >= currentBook.parts.length - 1) {
      this.completeBook(event);
      return;
    }
    this.moveForward(event);
  };

  completeBook = event => {
    event.preventDefault();
    event.stopPropagation();
    const { user, currentBook, history } = this.props;
    track({
      book: currentBook._id,
      data: [
        {
          event: { id: COMPLETED_BOOK },
          user: { id: user.uid },
          book: { id: currentBook._id, partIds: currentBook.parts.map(part => part._id) },
        },
      ],
    });
    ReactGA.event({
      category: 'Reading',
      action: 'Completed book',
    });
    // go to book review page if enabled by Author and user is not the Author
    if (currentBook.enableBookReviews && currentBook.author._id !== user.uid) {
      history.push(`/review?book=${currentBook._id}`);
    } else {
      history.push('/');
    }
  };

  submitAndMoveForward = async event => {
    event.preventDefault();
    event.stopPropagation();
    const idToken = await getFreshIdToken();

    const { currentBook, currentSpineIndex, submitSurveyAnswers, answers } =
      this.props;
    const surveyId = currentBook.parts[currentSpineIndex]._id;
    submitSurveyAnswers(
      idToken,
      {
        bookId: currentBook._id,
        surveyId,
        answers: answers[surveyId],
      },
      this.moveForward
    );
  };

  render() {
    const {
      t,
      currentSpineIndex,
      currentBook,
      standAlone,
      currentLanguage,
    } = this.props;
    const goToNextDisabled = !this.canMoveForward();

    const isAtStart = currentSpineIndex === 0;
    const isAtEnd = currentSpineIndex === currentBook.parts.length - 1;

    const goForwardIcon = 'chevron right';
    // const goForwardIcon = `chevron ${language.getSecondaryPosition(currentLanguage)}`;
    const goBackIcon = 'chevron left';
    // const goBackIcon = `chevron ${language.getPrimaryPosition(currentLanguage)}`;

    if (standAlone) {
      return (
        <div style={{ display: 'flow-root' }}>
          <Button
            variant="secondary"
            onClick={this.moveBack}
            disabled={isAtStart}
            style={{ float: language.getPrimaryPosition(currentLanguage) }}
          >
            <AppIcon name={goBackIcon} /> {t('Previous')}
          </Button>
          <Button
            variant="secondary"
            onClick={this.handleForward}
            disabled={goToNextDisabled}
            style={{ float: language.getSecondaryPosition(currentLanguage) }}
          >
            {!isAtEnd ? t('Next') : t('LeaveReview')}{' '}
            <AppIcon name={goForwardIcon} />
          </Button>
        </div>
      );
    }

    if (isAtStart) return null;
    return (
      <nav
        aria-label={t('Browse')}
        style={{ display: 'flex', gap: 8, maxWidth: 700, width: '100%' }}
      >
        <Button variant="secondary" onClick={this.moveBack} aria-label={t('Previous')}>
          <AppIcon name={goBackIcon} />
          <Media greaterThan="mobile"> {t('Previous')}</Media>
        </Button>
        <details>
          <summary>{t('Browse')}</summary>
          <Button variant="secondary" onClick={() => this.props.browseTo(0)}>
            {t('to beginning')}
          </Button>
          <Button variant="secondary" onClick={this.props.toggleChapterSelector}>
            {t('to chapter...')}
          </Button>
        </details>
        <Button
          variant="secondary"
          onClick={this.handleForward}
          disabled={goToNextDisabled}
          aria-label={!isAtEnd ? t('Next') : t('LeaveReview')}
          style={{ marginLeft: 'auto' }}
        >
          <Media greaterThan="mobile">{!isAtEnd ? t('Next') : t('LeaveReview')} </Media>
          <AppIcon name={goForwardIcon} />
        </Button>
      </nav>
    );
  }
}

// redux stuff
const mapStateToProps = state => ({
  user: state.user.user,
  idToken: state.user.idToken,
  userProfile: state.user.userProfile,
  currentBook: state.book.currentBook,
  readingData: state.readerApp.readingData,
  currentSpineIndex: state.readerApp.currentSpineIndex,
  answers: state.survey.answers,
  defaultColorMode: state.app.defaultColorMode,
  currentLanguage: state.user.currentLanguage,
});

const mapDispatchToProps = dispatch =>
  bindActionCreators(
    {
      changePage: newPage => push(newPage),
      updateSpineIndex,
      submitSurveyAnswers,
    },
    dispatch
  );

export default withTranslation()(
  withRouter(connect(mapStateToProps, mapDispatchToProps)(ChapterMenu))
);
